import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";
import axios from "axios";
import { errMsg } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { BrandLogo } from "../components/BrandLogo";
import { ShieldCheck, DatabaseBackup, FileLock2, ChevronDown } from "lucide-react";
import { InstallAppButton } from "../components/InstallAppButton";

const InfoBlock = ({ icon: Icon, titleKey, bodyKey, t, id }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border" data-testid={`info-${id}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 p-3 text-left hover:bg-muted/50 transition-colors"
        data-testid={`info-${id}-toggle`}
      >
        <Icon size={14} className="text-primary shrink-0" />
        <span className="text-[11px] font-bold uppercase tracking-widest flex-1">{t(titleKey)}</span>
        <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-3 pb-3 text-[11px] leading-relaxed text-muted-foreground">
          {t(bodyKey)}
        </div>
      )}
    </div>
  );
};

export default function Login() {
  const { login } = useAuth();
  const { t, lang, setLang } = useI18n();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [brand, setBrand] = useState({
    company_name: "MFIS Supply-Chain by OOMINING",
    logo_mark: "M",
    app_subtitle: "Supply-Chain · Procurement & WMS",
    brand_tagline: "by OOMINING",
  });

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/public/branding`)
      .then((r) => setBrand(r.data))
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const u = await login(email, password);
      toast.success(`${t("login.success")}, ${u.name}`);
      navigate("/");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex flex-col justify-center px-8 sm:px-16 lg:px-20 py-12 bg-background overflow-y-auto">
        <div className="flex items-center gap-4 mb-10">
          <BrandLogo version={brand.app_logo_version} className="h-16" testid="login-logo" />
          <div className="min-w-0 border-l pl-4">
            <div className="font-black tracking-tight uppercase text-sm leading-tight truncate" data-testid="login-brand-name">{brand.company_name}</div>
            {brand.brand_tagline && (
              <div className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest truncate" data-testid="login-brand-tagline">{brand.brand_tagline}</div>
            )}
            {brand.app_subtitle && (
              <div className="text-[10px] text-muted-foreground/70 uppercase tracking-[0.15em] truncate" data-testid="login-brand-subtitle">{brand.app_subtitle}</div>
            )}
          </div>
          <div className="ml-auto flex border">
            {["es", "en"].map((l) => (
              <button key={l} onClick={() => setLang(l)} data-testid={`login-lang-${l}`}
                className={`px-2.5 py-1 text-[10px] font-bold uppercase ${lang === l ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>{l}</button>
            ))}
          </div>
        </div>
        <h1 className="text-4xl font-black tracking-tighter mb-1">{t("login.title")}</h1>
        <p className="text-sm text-muted-foreground mb-8">{t("login.subtitle")}</p>
        <form onSubmit={submit} className="space-y-4 max-w-sm">
          <div className="space-y-1.5">
            <Label htmlFor="email" className="text-xs font-bold uppercase tracking-widest">{t("login.email")}</Label>
            <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email-input" className="rounded-none" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password" className="text-xs font-bold uppercase tracking-widest">{t("login.password")}</Label>
            <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password-input" className="rounded-none" />
          </div>
          {error && <div data-testid="login-error" className="text-xs text-destructive border border-destructive/40 bg-destructive/5 p-2">{error}</div>}
          <Button type="submit" disabled={busy} data-testid="login-submit-button"
            className="w-full rounded-none font-bold uppercase tracking-widest active:scale-95 transition-transform">
            {t("login.submit")}
          </Button>
          <div className="flex items-center pt-1">
            <Link to="/forgot-password" data-testid="login-forgot-link"
              className="text-[11px] font-bold uppercase tracking-widest text-primary hover:underline">
              {t("login.forgot")}
            </Link>
          </div>
        </form>

        <div className="mt-10 max-w-sm space-y-2" data-testid="login-info-sections">
          <InfoBlock icon={FileLock2} titleKey="info.privacy.title" bodyKey="info.privacy.body" t={t} id="privacy" />
          <InfoBlock icon={ShieldCheck} titleKey="info.security.title" bodyKey="info.security.body" t={t} id="security" />
          <InfoBlock icon={DatabaseBackup} titleKey="info.backups.title" bodyKey="info.backups.body" t={t} id="backups" />
        </div>

        <div className="mt-8 max-w-sm">
          <InstallAppButton variant="text" />
        </div>

        <div className="mt-10 text-[10px] text-muted-foreground max-w-sm">
          © {new Date().getFullYear()} OOMINING - mining software · Chihuahua, México
        </div>
      </div>
      <div className="hidden lg:block relative">
        <img src="https://images.unsplash.com/photo-1721938170874-318bdeddea6c?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="warehouse" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/60" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <div className="text-3xl font-black tracking-tighter leading-tight">
            {t("login.hero1")}
          </div>
          <div className="text-sm text-white/70 mt-2">
            {t("login.hero2")}
          </div>
        </div>
      </div>
    </div>
  );
}
